import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useColorMode } from "@chakra-ui/react";

// Enhanced position options for maximum accuracy
const HIGH_ACCURACY_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 0, // Never use cached positions
  timeout: 15000 // 15 seconds per attempt
};

const STANDARD_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  maximumAge: 30000, // Allow 30-second-old positions
  timeout: 10000 // 10 seconds
};

type CachedLocationSnapshot = {
  position: { latitude: number; longitude: number };
  accuracy: number | null;
  method: string;
  address: string;
  timestamp: number;
};

const LOCATION_CACHE_KEY = "zensight.location-access.snapshot";
const LOCATION_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

const readCachedLocation = (): CachedLocationSnapshot | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(LOCATION_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const timestamp = typeof parsed.timestamp === "number" ? parsed.timestamp : 0;
    if (!timestamp || Date.now() - timestamp > LOCATION_CACHE_TTL_MS) {
      window.sessionStorage.removeItem(LOCATION_CACHE_KEY);
      return null;
    }

    const position = parsed.position;
    if (
      !position ||
      typeof position.latitude !== "number" ||
      typeof position.longitude !== "number"
    ) {
      return null;
    }

    return {
      position: {
        latitude: position.latitude,
        longitude: position.longitude
      },
      accuracy: typeof parsed.accuracy === "number" ? parsed.accuracy : null,
      method: typeof parsed.method === "string" ? parsed.method : "",
      address: typeof parsed.address === "string" ? parsed.address : "",
      timestamp
    };
  } catch (error) {
    console.warn("Failed to read cached location:", error);
    return null;
  }
};

const writeCachedLocation = (snapshot: Omit<CachedLocationSnapshot, "timestamp">) => {
  if (typeof window === "undefined") return;
  try {
    const payload: CachedLocationSnapshot = {
      ...snapshot,
      timestamp: Date.now()
    };
    window.sessionStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn("Failed to cache location:", error);
  }
};

const clearCachedLocation = () => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(LOCATION_CACHE_KEY);
  } catch (error) {
    console.warn("Failed to clear cached location:", error);
  }
};

const SUBMIT_PENDING_KEY = "zensight.location-access.submitting";

const readPendingSubmit = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(SUBMIT_PENDING_KEY) === "1";
  } catch (error) {
    console.warn("Failed to read pending submit state:", error);
    return false;
  }
};

const writePendingSubmit = (pending: boolean) => {
  if (typeof window === "undefined") return;
  try {
    if (pending) {
      window.sessionStorage.setItem(SUBMIT_PENDING_KEY, "1");
    } else {
      window.sessionStorage.removeItem(SUBMIT_PENDING_KEY);
    }
  } catch (error) {
    console.warn("Failed to persist pending submit state:", error);
  }
};

const scheduleAfterPaint = (callback: () => void) => {
  if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(() => callback());
    return;
  }
  setTimeout(() => callback(), 0);
};

// Improved position fetching with multiple strategies
class LocationService {
  private static async getPositionWithOptions(options: PositionOptions): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });
  }

  // Strategy 1: High-accuracy GPS with multiple attempts
  static async getHighAccuracyPosition(): Promise<GeolocationPosition> {
    let bestPosition: GeolocationPosition | null = null;
    let bestAccuracy = Infinity;
    const maxAttempts = 3;
    
    console.log("🎯 Starting high-accuracy GPS positioning...");
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        console.log(`📍 GPS attempt ${attempt}/${maxAttempts}`);
        const position = await this.getPositionWithOptions(HIGH_ACCURACY_OPTIONS);
        const accuracy = position.coords.accuracy || Infinity;
        
        console.log(`✅ GPS attempt ${attempt}: ${accuracy.toFixed(1)}m accuracy`);
        
        if (accuracy < bestAccuracy) {
          bestPosition = position;
          bestAccuracy = accuracy;
        }
        
        // If we get very good accuracy, stop trying
        if (accuracy <= 10) {
          console.log(`🎉 Excellent accuracy achieved: ${accuracy.toFixed(1)}m`);
          return position;
        }
        
        // Add a small delay between attempts
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
        
      } catch (error) {
        console.warn(`❌ GPS attempt ${attempt} failed:`, error);
        if (attempt === maxAttempts && !bestPosition) {
          throw error;
        }
      }
    }
    
    if (bestPosition) {
      console.log(`✅ Using best GPS position: ${bestAccuracy.toFixed(1)}m accuracy`);
      return bestPosition;
    }
    
    throw new Error("No GPS position obtained");
  }

  // Strategy 2: Standard GPS (faster, less accurate)
  static async getStandardPosition(): Promise<GeolocationPosition> {
    console.log("📡 Trying standard GPS...");
    const position = await this.getPositionWithOptions(STANDARD_OPTIONS);
    console.log(`✅ Standard GPS: ${(position.coords.accuracy || 0).toFixed(1)}m accuracy`);
    return position;
  }

  // Strategy 3: Network-based location (IP geolocation)
  static async getNetworkPosition(): Promise<{ latitude: number; longitude: number; accuracy: number }> {
    console.log("🌐 Using network-based location...");
    
    const services = [
      {
        url: 'https://ipapi.co/json/',
        parser: (data: any) => data.latitude && data.longitude ? {
          latitude: data.latitude,
          longitude: data.longitude,
          accuracy: 5000 // Network accuracy is typically 1-5km
        } : null
      },
      {
        url: 'https://ip-api.com/json/?fields=lat,lon,status',
        parser: (data: any) => data.status === 'success' && data.lat && data.lon ? {
          latitude: data.lat,
          longitude: data.lon,
          accuracy: 5000
        } : null
      }
    ];

    for (const service of services) {
      try {
        const response = await fetch(service.url, { timeout: 5000 } as any);
        const data = await response.json();
        const result = service.parser(data);
        
        if (result) {
          console.log(`✅ Network location: ${result.accuracy}m accuracy`);
          return result;
        }
      } catch (error) {
        console.warn("Network service failed:", error);
      }
    }
    
    throw new Error("Network location failed");
  }

  // Enhanced address resolution with multiple services
  static async getAddressFromCoordinates(lat: number, lon: number): Promise<string> {
    const services = [
      // Primary service with detailed addressing
      {
        name: "Nominatim Detailed",
        url: `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1&accept-language=en`,
        parser: (data: any) => {
          if (data?.address) {
            const a = data.address;
            const parts = [
              a.house_number && a.road ? `${a.house_number} ${a.road}` : a.road,
              a.suburb || a.neighbourhood || a.hamlet,
              a.city || a.town || a.village,
              a.state || a.region,
              a.postcode,
              a.country
            ].filter(Boolean);
            
            return parts.length >= 2 ? parts.join(", ") : data.display_name;
          }
          return null;
        }
      },
      // Fallback service
      {
        name: "Nominatim Simple", 
        url: `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=16&addressdetails=1`,
        parser: (data: any) => data?.display_name || null
      }
    ];

    for (const service of services) {
      try {
        console.log(`🔍 Trying ${service.name} for address...`);
        const response = await fetch(service.url, {
          headers: { 'User-Agent': 'GeoSeekPro/1.0' }
        });
        
        const data = await response.json();
        const address = service.parser(data);
        
        if (address) {
          console.log(`✅ ${service.name} success`);
          return address;
        }
      } catch (error) {
        console.warn(`❌ ${service.name} failed:`, error);
      }
    }
    
    return `Location: ${lat.toFixed(6)}, ${lon.toFixed(6)}`;
  }

  // Main location fetching method with smart fallbacks
  static async getCurrentLocation(): Promise<{
    position: { latitude: number; longitude: number };
    accuracy: number;
    method: string;
    address: string;
  }> {
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      throw new Error("Geolocation is not supported by this browser");
    }

    // Check permissions first
    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        if (permission.state === 'denied') {
          throw new Error("Location permission denied. Please enable location access in your browser settings.");
        }
      } catch (error) {
        console.warn("Permission check failed:", error);
      }
    }

    let position: { latitude: number; longitude: number };
    let accuracy: number;
    let method: string;

    try {
      // Strategy 1: High-accuracy GPS
      const gpsPosition = await this.getHighAccuracyPosition();
      position = {
        latitude: gpsPosition.coords.latitude,
        longitude: gpsPosition.coords.longitude
      };
      accuracy = gpsPosition.coords.accuracy || 999999;
      method = accuracy <= 20 ? "High-Precision GPS" : "GPS";
      
    } catch (gpsError) {
      console.warn("High-accuracy GPS failed:", gpsError);
      
      try {
        // Strategy 2: Standard GPS
        const standardPosition = await this.getStandardPosition();
        position = {
          latitude: standardPosition.coords.latitude,
          longitude: standardPosition.coords.longitude
        };
        accuracy = standardPosition.coords.accuracy || 999999;
        method = "Standard GPS";
        
      } catch (standardError) {
        console.warn("Standard GPS failed:", standardError);
        
        try {
          // Strategy 3: Network location
          const networkPosition = await this.getNetworkPosition();
          position = {
            latitude: networkPosition.latitude,
            longitude: networkPosition.longitude
          };
          accuracy = networkPosition.accuracy;
          method = "Network Location";
          
        } catch (networkError) {
          console.error("All location methods failed:", networkError);
          throw new Error("Unable to determine your location. Please check your permissions and internet connection.");
        }
      }
    }

    // Get address
    const address = await this.getAddressFromCoordinates(position.latitude, position.longitude);

    return { position, accuracy, method, address };
  }
}

// Enhanced autocomplete search
async function searchLocations(query: string, signal?: AbortSignal): Promise<any[]> {
  if (query.length < 3) return [];
  
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?` +
      new URLSearchParams({
        q: query,
        format: 'json',
        addressdetails: '1',
        limit: '8',
        countrycodes: 'in', // Adjust based on your target region
        bounded: '1',
        viewbox: '68.0,37.5,97.5,6.0' // India bounding box - adjust as needed
      }),
      { 
        signal,
        headers: { 'User-Agent': 'GeoSeekPro/1.0' }
      }
    );
    
    const data = await response.json();
    
    // Enhanced sorting by relevance and type
    return data.sort((a: any, b: any) => {
      const aImportance = parseFloat(a.importance || 0);
      const bImportance = parseFloat(b.importance || 0);
      
      // Prefer more specific location types
      const getTypeScore = (item: any) => {
        const type = item.type || '';
        if (type.includes('house') || type.includes('building')) return 10;
        if (type.includes('road') || type.includes('street')) return 8;
        if (type.includes('suburb') || type.includes('neighbourhood')) return 6;
        if (type.includes('city') || type.includes('town')) return 4;
        return 1;
      };
      
      const aScore = aImportance + getTypeScore(a);
      const bScore = bImportance + getTypeScore(b);
      
      return bScore - aScore;
    });
  } catch (error) {
    if (error instanceof Error && error.name !== 'AbortError') {
      console.error("Search failed:", error);
    }
    return [];
  }
}

type LocationAccessProps = {
  onLocation: (location: { latitude: number; longitude: number }, address: string) => void;
  onError: (error: string) => void;
  onCancel: () => void;
  submittingMessage?: string;
};

const LocationAccess: React.FC<LocationAccessProps> = ({ onLocation, onError, onCancel, submittingMessage }) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const initialSnapshot = useMemo(() => readCachedLocation(), []);
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(
    initialSnapshot?.position ?? null
  );
  const [address, setAddress] = useState<string>(initialSnapshot?.address ?? "");
  const [query, setQuery] = useState<string>(initialSnapshot?.address ?? "");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(!initialSnapshot);
  const initialPendingSubmit = useMemo(() => readPendingSubmit(), []);
  const [submitting, setSubmitting] = useState(initialPendingSubmit);
  const [locationAccuracy, setLocationAccuracy] = useState<number | null>(initialSnapshot?.accuracy ?? null);
  const [locationMethod, setLocationMethod] = useState<string>(initialSnapshot?.method ?? "");
  const [loadingStatus, setLoadingStatus] = useState<string>(
    initialSnapshot ? "Location ready" : "Initializing location services..."
  );
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const suppressNextSearch = useRef<boolean>(false);
  const hasFetchedLocation = useRef<boolean>(!!initialSnapshot);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      writePendingSubmit(false);
    };
  }, []);

  const persistLocationSnapshot = useCallback(
    (snapshot: { position: { latitude: number; longitude: number }; accuracy: number | null; method: string; address: string }) => {
      writeCachedLocation(snapshot);
      hasFetchedLocation.current = true;
    },
    []
  );

  // Ensure pending submit state is cleared if no submission is in progress
  useEffect(() => {
    if (!submitting) {
      writePendingSubmit(false);
    }
  }, [submitting]);

  // Debounced search effect
  useEffect(() => {
    const controller = new AbortController();
    
    const performSearch = async () => {
      if (suppressNextSearch.current) {
        suppressNextSearch.current = false;
        return;
      }
      if (query.trim().length < 3) {
        setSuggestions([]);
        return;
      }
      
      try {
        const results = await searchLocations(query.trim(), controller.signal);
        setSuggestions(results);
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error("Search error:", error);
        }
      }
    };
    
    const debounceTimer = setTimeout(performSearch, 300);
    
    return () => {
      clearTimeout(debounceTimer);
      controller.abort();
    };
  }, [query]);

  // Main location fetching effect
  useEffect(() => {
    if (hasFetchedLocation.current) return;
    hasFetchedLocation.current = true;

    const fetchLocation = async () => {
      try {
        setLoadingStatus("Checking location permissions...");
        await new Promise(resolve => setTimeout(resolve, 500)); // Small delay for UX
        
        setLoadingStatus("Getting your precise location...");
        
        const result = await LocationService.getCurrentLocation();
        
        setLocation(result.position);
        setLocationAccuracy(result.accuracy);
        setLocationMethod(result.method);
        setLoadingStatus("Resolving address...");
        persistLocationSnapshot({
          position: result.position,
          accuracy: result.accuracy,
          method: result.method,
          address: result.address
        });
        
        setAddress(result.address);
        setQuery(result.address);
        setLoading(false);
        
        console.log("✅ Location successfully obtained:", {
          position: result.position,
          accuracy: result.accuracy,
          method: result.method
        });
        
      } catch (error) {
        console.error("Location fetch failed:", error);
        setLoading(false);
        clearCachedLocation();
        onError(error instanceof Error ? error.message : "Failed to get location");
      }
    };

    fetchLocation();
  }, [onError, persistLocationSnapshot]);

  const handleSuggestionSelect = useCallback((suggestion: any) => {
    const displayName = suggestion.display_name;
    suppressNextSearch.current = true;
    setQuery(displayName);
    setAddress(displayName);
    setSuggestions([]);
    setShowSuggestions(false);
    setLocation({ 
      latitude: parseFloat(suggestion.lat), 
      longitude: parseFloat(suggestion.lon) 
    });
    setLocationAccuracy(null);
    setLocationMethod("Manual Selection");
    persistLocationSnapshot({
      position: {
        latitude: parseFloat(suggestion.lat),
        longitude: parseFloat(suggestion.lon)
      },
      accuracy: null,
      method: "Manual Selection",
      address: displayName
    });
  }, [persistLocationSnapshot]);

  const handleConfirmLocation = useCallback(() => {
    if (!location || submitting) return;
    setSubmitting(true);
    writePendingSubmit(true);
    setShowSuggestions(false);
    const executeConfirmation = async () => {
      try {
        await Promise.resolve(onLocation(location, query || address));
      } catch (error) {
        console.error("Location confirmation failed:", error);
        if (error instanceof Error) {
          onError(error.message);
        } else {
          onError("Failed to confirm location. Please try again.");
        }
        if (isMountedRef.current) {
          setSubmitting(false);
        }
        writePendingSubmit(false);
      }
    };

    scheduleAfterPaint(() => {
      void executeConfirmation();
    });
  }, [location, query, address, onLocation, submitting, onError]);

  const getAccuracyColor = (accuracy: number | null) => {
    if (!accuracy) return "text-gray-500";
    if (accuracy <= 10) return "text-green-600";
    if (accuracy <= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const getAccuracyText = (accuracy: number | null) => {
    if (!accuracy) return "Unknown accuracy";
    if (accuracy <= 10) return "Excellent accuracy";
    if (accuracy <= 50) return "Good accuracy";
    if (accuracy <= 200) return "Moderate accuracy";
    return "Low accuracy";
  };

  const getMethodIcon = (method: string) => {
    if (method.includes("GPS")) return "🛰️";
    if (method.includes("Network")) return "🌐";
    return "📍";
  };

  if (loading) {
    return (
      <div className="text-center max-w-md mx-auto">
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-[#E8C16C] border-opacity-30 border-t-[#B59143] rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-4 h-4 bg-[#B59143] rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
        
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'} mb-6 animate-pulse`}>
          {loadingStatus}
        </h3>
        
        <button
          onClick={onCancel}
          className={`${isDark ? 'px-6 py-2 rounded-lg bg-[#2A3441] text-white border border-[#2E2F33] hover:bg-[#3A4553]' : 'px-6 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200'} font-medium transition-colors`}
        >
          Cancel
        </button>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="text-center max-w-md mx-auto">
        <div className="text-red-500 mb-6">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDark ? 'bg-red-900/30' : 'bg-red-100'}`}>
            <span className="text-2xl">❌</span>
          </div>
          <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : ''}`}>Location Access Failed</h3>
        </div>
        
        <div className={`${isDark ? 'bg-[#160E0E] border-red-900/50' : 'bg-red-50 border-red-200'} border rounded-lg p-4 mb-6 text-left`}>
          <h4 className={`font-medium mb-3 ${isDark ? 'text-red-300' : 'text-red-800'}`}>🔧 Try These Solutions:</h4>
          <ol className={`text-sm space-y-2 list-decimal list-inside ${isDark ? 'text-red-300' : 'text-red-700'}`}>
            <li>Click the location icon in your browser's address bar</li>
            <li>Select "Allow" for location access</li>
            <li>Refresh the page and try again</li>
            <li>Check your device's location settings</li>
            <li>Try a different browser if issues persist</li>
          </ol>
        </div>
        
        <button
          onClick={onCancel}
          className={`${isDark ? 'px-6 py-3 rounded-lg bg-[#2A3441] text-white border border-[#2E2F33] hover:bg-[#3A4553]' : 'px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200'} font-medium transition-colors`}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="text-center max-w-lg mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="text-2xl">{getMethodIcon(locationMethod)}</span>
          <div className="text-center">
            <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>{locationMethod}</div>
            {locationAccuracy && (
              <div className={`text-xs ${getAccuracyColor(locationAccuracy)}`}>
                {getAccuracyText(locationAccuracy)} (±{Math.round(locationAccuracy)}m)
              </div>
            )}
          </div>
        </div>
        
        {/* Search/verify location */}
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>
            📍 Search or verify your location:
          </label>
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className={`w-full px-4 py-3 rounded-lg outline-none focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] selection:bg-[#FFF7E6] ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] placeholder-gray-400' : 'bg-white text-gray-900 border border-gray-300 placeholder-gray-500'}`}
              placeholder="Search for your exact location..."
            />
            
            {showSuggestions && suggestions.length > 0 && (
              <div className={`absolute z-10 w-full mt-1 rounded-lg shadow-lg max-h-48 overflow-y-auto ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33] text-white' : 'bg-white border border-gray-200'}`}>
                {suggestions.map((suggestion, index) => (
                  <button
                    key={suggestion.place_id || index}
                    onClick={() => handleSuggestionSelect(suggestion)}
                    className={`w-full px-4 py-3 text-left border-b last:border-b-0 transition-colors ${isDark ? 'border-[#2E2F33] hover:bg-[#2A3441]' : 'border-gray-100 hover:bg-[#FFF7E6]'}`}
                  >
                    <div className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {suggestion.display_name.split(',').slice(0, 2).join(', ')}
                    </div>
                    <div className={`text-xs mt-1 ${isDark ? 'text-[#C3C9D3]' : 'text-gray-500'}`}>
                      {suggestion.display_name.split(',').slice(2).join(', ')}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Current address display */}
        <div className={`rounded-lg p-4 mb-6 border ${isDark ? 'bg-[#0F1216] border-[#2E2F33]' : 'bg-gray-50 border-gray-200'}`}>
          <div className={`text-sm ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>
            <strong>Current Location:</strong>
          </div>
          <div className={`${isDark ? 'text-white' : 'text-gray-800'} mt-1`}>{address}</div>
        </div>
        
        {/* Accuracy warning */}
        {locationAccuracy && locationAccuracy > 100 && (
          <div className={`rounded-lg p-4 mb-6 border ${isDark ? 'bg-[#1D1A11] border-yellow-900/40' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="flex items-start gap-2">
              <span className={`text-lg ${isDark ? 'text-yellow-300' : 'text-yellow-600'}`}>⚠️</span>
              <div className="text-left">
                <p className={`text-sm font-medium mb-1 ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>
                  Location accuracy could be improved
                </p>
                <p className={`text-xs ${isDark ? 'text-yellow-300/80' : 'text-yellow-700'}`}>
                  Consider searching for your exact address above for better precision.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Action buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleConfirmLocation}
          disabled={submitting}
          className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all ${
            submitting 
              ? "bg-[#E8C16C] text-white cursor-not-allowed opacity-70" 
              : "bg-gradient-to-r from-[#FBD282] to-[#B59143] hover:from-[#E8C16C] hover:to-[#A7843D] text-white transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#B59143]"
          }`}
        >
          {submitting ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              {submittingMessage || "Confirming..."}
            </div>
          ) : (
            "✓ Confirm Location"
          )}
        </button>
        
        <button
          onClick={onCancel}
          disabled={submitting}
          className={`${isDark ? 'px-6 py-3 rounded-lg bg-[#2A3441] text-white border border-[#2E2F33] hover:bg-[#3A4553]' : 'px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200'} font-semibold transition-colors disabled:opacity-50`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default LocationAccess;